import { useCallback, useState, type ReactNode } from 'react';
import { ReportPreviewDialog } from './ReportPreviewDialog';
import { ReportHistoryDialog } from './ReportHistoryDialog';

/**
 * One-liner wiring for the report dialogs on a test workflow page:
 *
 *   const { openPreview, openHistory, previewDialog } = useReportPreview(workOrderDetailKey);
 *   <TestActionBar onPreviewReport={openPreview} onReportHistory={openHistory} ... />
 *   {previewDialog}
 *
 * `previewDialog` carries BOTH dialogs (Preview & Edit + standalone Report
 * Archive). Each mounts only while open, so the PDF blob is not
 * touched until the user actually asks for a preview.
 */
export function useReportPreview(workOrderDetailKey: string | null | undefined): {
  openPreview: () => void;
  openHistory: () => void;
  previewDialog: ReactNode;
} {
  const [open, setOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const openPreview = useCallback(() => setOpen(true), []);
  const openHistory = useCallback(() => setHistoryOpen(true), []);
  const close = useCallback(() => setOpen(false), []);
  const closeHistory = useCallback(() => setHistoryOpen(false), []);

  const previewDialog = workOrderDetailKey ? (
    <>
      {open && (
        <ReportPreviewDialog open onClose={close} workOrderDetailKey={workOrderDetailKey} />
      )}
      {historyOpen && (
        <ReportHistoryDialog open onClose={closeHistory} workOrderDetailKey={workOrderDetailKey} />
      )}
    </>
  ) : null;

  return { openPreview, openHistory, previewDialog };
}
